/**
 * Timestamp formatting utilities — DEC-004 / §44 compliance.
 *
 * ALL timestamp rendering in web-billing MUST go through this module. NEVER call
 * `.toLocaleTimeString()` / `.toLocaleString()` bare — they may display UTC.
 * NEVER call `.toUTCString()`.
 *
 * Copied from packages/web-dispatch/src/lib/timestamp.ts (Phase 12 / Task 12.3),
 * trimmed to the formatters the billing console needs (the map-pin staleness
 * helpers are dispatch-only). The REAL timezone comes from
 * GET /v1/users/me/profile via useUserTimezone(); DEFAULT_TZ is ONLY a
 * pre-resolution fallback.
 *
 * Spec: §44 (local-by-context timestamp rendering); DEC-004.
 */

/** The application fallback timezone when the user profile timezone is not yet loaded. */
export const DEFAULT_TIMEZONE = 'America/Chicago';

/**
 * Alias for DEFAULT_TIMEZONE — used by the use-user-timezone hook (DEC-004
 * pre-resolution fallback only).
 */
export const DEFAULT_TZ = DEFAULT_TIMEZONE;

/**
 * Format an ISO timestamp string for display (date + time).
 *
 * Uses `Intl.DateTimeFormat` with an explicit `timeZone` — never bare
 * `toLocaleTimeString()` or `toUTCString()` (§44 / DEC-004 hard requirement).
 *
 * @param isoString  ISO 8601 timestamp string (e.g. "2026-06-13T14:30:00Z").
 * @param timeZone   IANA timezone identifier. Defaults to 'America/Chicago'.
 * @returns Formatted date+time string in the given timezone (e.g. "Jun 13, 2:30 PM").
 */
export function formatTimestamp(isoString: string, timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(date);
  } catch {
    return '—';
  }
}

/**
 * Format an ISO timestamp as a date-only string (e.g. "Jun 13, 2026").
 * Used for invoice billing-period bounds where the time-of-day is not meaningful.
 *
 * @param isoString  ISO 8601 timestamp string.
 * @param timeZone   IANA timezone identifier. Defaults to 'America/Chicago'.
 */
export function formatDate(isoString: string, timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone,
    }).format(date);
  } catch {
    return '—';
  }
}

/**
 * Format a CALENDAR date ("YYYY-MM-DD", e.g. an invoice due date) as "Nov 11, 2026".
 *
 * A due date is already a local day (DEC-326: the tenant-local send day + terms), not
 * an instant, so it must not pass through any timezone conversion: `new Date('2026-11-11')`
 * is UTC midnight, which formats as Nov 10 anywhere west of UTC. Rendering it in UTC
 * from its own parts keeps the day the server chose.
 */
export function formatCalendarDate(yyyyMmDd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd);
  if (m === null) return '—';
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/** "Net 30", or "Due on receipt" for 0 days (DEC-326). */
export function formatPaymentTerms(days: number): string {
  return days === 0 ? 'Due on receipt' : `Net ${String(days)}`;
}

/**
 * Format an ISO timestamp in the "May 23, 2026 14:32"-style for Generated columns.
 *
 * DEC-004 compliant — uses Intl.DateTimeFormat with explicit timeZone.
 *
 * @param isoString  ISO 8601 timestamp string.
 * @param timeZone   IANA timezone identifier. Defaults to DEFAULT_TZ (Chicago).
 */
export function formatCreated(isoString: string, timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    const datePart = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone,
    }).format(date);
    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone,
    }).format(date);
    return `${datePart} ${timePart}`;
  } catch {
    return '—';
  }
}

/**
 * Date, time and the zone's short name ("Sep 14, 2026, 12:05 PM CDT") — for an audit
 * history, where a reader comparing entries needs to see which zone a time is in (#50).
 * DEC-004: explicit timeZone, never bare toLocale*.
 */
export function formatTimestampWithZone(
  isoString: string,
  timeZone: string = DEFAULT_TIMEZONE,
): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone,
    }).format(date);
  } catch {
    return '—';
  }
}
